import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import HtmlPreview from "@entrilia/shared/components/html/HtmlPreview";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { InboxItem } from "../../../../../api/types/inboxesTypes";

interface Props {
  workflowId: string;
}

const formatNameAndAddress = (fullName: string, userName: string, address: string) => {
  let result = fullName;
  if (userName && !result.includes(userName)) {
    result = `${userName} ${result}`;
  }
  if (address && !result.includes(address)) {
    result += ` <${address}>`;
  }
  return result;
};

const formatFrom = (inboxItem: InboxItem) =>
  formatNameAndAddress(inboxItem.from, inboxItem.fromUserName || "", inboxItem.fromAddress || "");

const formatTo = (inboxItem: InboxItem) =>
  formatNameAndAddress(inboxItem.to, inboxItem.toUserName || "", inboxItem.toAddress || "");

const WorkflowEmailPreviewContent = ({ workflowId }: Props) => {
  const getWorkflowEmailDetails = useCallback(() => adminApi.getWorkflowTriggerEmailDetails(workflowId), [workflowId]);

  const [emailDetails, fetchError] = useFetch(getWorkflowEmailDetails);

  useEffect(() => {
    if (fetchError) {
      logError(fetchError, "[WorkflowEmailPreviewDrawerContent] getWorkflowTriggerEmailDetails");
    }
  }, [fetchError]);

  if (fetchError) {
    return <DataLoadingFailed title="Failed to load workflow email content" />;
  }

  if (emailDetails === undefined) {
    return <InlineLoader />;
  }

  const { inboxItem, emailHtmlContent } = emailDetails;

  return (
    <Box sx={{ overflow: "auto", flex: 1, p: 3 }}>
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <PersonIcon color="action" />
            <Typography color="textSecondary">From</Typography>
            <Typography>{formatFrom(inboxItem)}</Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <PersonIcon color="action" />
            <Typography color="textSecondary">To</Typography>
            <Typography>{formatTo(inboxItem)}</Typography>
          </Stack>
        </Stack>

        <Divider flexItem />

        <Paper variant="outlined">
          {inboxItem.subject && <Typography sx={{ py: 1, px: 1.5 }}>{inboxItem.subject}</Typography>}
          {inboxItem.subject && <Divider />}
          <HtmlPreview htmlContent={emailHtmlContent} containerProps={{ sx: { p: 1.5 } }} />
        </Paper>
      </Stack>
    </Box>
  );
};

export default WorkflowEmailPreviewContent;
